import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, InputGroup, Spinner, Alert } from 'react-bootstrap';
import {
  FaUserShield,
  FaSearch,
  FaUser,
  FaPaperPlane,
  FaPhone,
  FaVideo,
  FaUsers,
  FaPaperclip,
  FaSmile,
  FaEllipsisV,
  FaCircle,
  FaComments,
  FaExclamationTriangle,
  FaArchive,
  FaCheckCircle,
  FaClock,
  FaFilter,
  FaUserTie,
  FaRobot,
  FaTimes
} from 'react-icons/fa';

import { useChat } from '../../../contexts/ChatContext';
import { useMessages } from '../../../contexts/MessageContext';
import { useAuth } from '../../../contexts/AuthContext';
import { getAdmins } from '../../../services/adminService';
import './AdminChatPanel.css';

const AdminChatPanel = () => {
  const { sendMessage } = useChat();
  const { conversations: messageThreads, sendMessage: sendThreadMessage, fetchThreadById } = useMessages();
  const { currentUser } = useAuth();

  // Enhanced state management
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [adminTakeovers, setAdminTakeovers] = useState({});
  const [unreadCounts, setUnreadCounts] = useState({});
  const [typingUsers, setTypingUsers] = useState({});
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [adminCount, setAdminCount] = useState(0);
  const messageEndRef = useRef(null);
  const websocketRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // connected, disconnected, reconnecting
  const [lastMessageId, setLastMessageId] = useState(null);

  // Quick reply templates
  const quickReplies = [
    { id: 1, text: "I'll help you with that right away.", category: "helpful" },
    { id: 2, text: "Please wait a moment while I check that for you.", category: "waiting" },
    { id: 3, text: "Thank you for your patience.", category: "gratitude" },
    { id: 4, text: "I understand your concern. Let me assist you.", category: "empathy" },
    { id: 5, text: "Can you provide more details about this issue?", category: "clarification" },
    { id: 6, text: "I'm escalating this to our technical team.", category: "escalation" }
  ];

  // Debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((term) => setSearchTerm(term), 300),
    []
  );

  // Performance metrics
  const performanceMetrics = useMemo(() => {
    const avgResponseTime = 2.3; // minutes
    const customerSatisfaction = 4.8;
    const conversationsPerHour = 12;
    
    return { avgResponseTime, customerSatisfaction, conversationsPerHour };
  }, []);

  // Fetch admin count using same authentication logic
  const fetchAdminCount = useCallback(async () => {
    try {
      console.log('🔍 Attempting to fetch admin count from Django backend...');
      const admins = await getAdmins();
      const count = Array.isArray(admins) ? admins.length : 0;
      
      if (count === 0) {
        console.log('⚠️ Django backend shows 0 admins, using authentication logic...');
        // Use the same authentication logic that protects admin routes
        const isAdminOrStaff = currentUser?.role === 'admin' || 
                              currentUser?.is_staff || 
                              currentUser?.is_superuser;
        
        if (isAdminOrStaff) {
          setAdminCount(1);
          console.log('👥 Using authentication logic: Current user is admin (1)');
          console.log('🔐 Admin verification:', {
            role: currentUser?.role,
            is_staff: currentUser?.is_staff,
            is_superuser: currentUser?.is_superuser,
            isAdmin: isAdminOrStaff
          });
          return 1;
        } else {
          setAdminCount(0);
          console.log('👥 No admin users detected via authentication');
          return 0;
        }
      } else {
        setAdminCount(count);
        console.log(`👥 Found ${count} admin(s) in the system:`, admins);
        return count;
      }
    } catch (error) {
      console.error('❌ Error fetching admin count:', error);
      console.log('📝 Error details:', error.response?.data || error.message);
      
      // Fallback: Use the same authentication logic that protects admin routes
      const isAdminOrStaff = currentUser?.role === 'admin' || 
                            currentUser?.is_staff || 
                            currentUser?.is_superuser;
      
      if (isAdminOrStaff) {
        setAdminCount(1);
        console.log('👥 Fallback: Using authentication logic - Current user is admin (1)');
        return 1;
      } else {
        setAdminCount(0);
        console.log('👥 No admin users detected');
        return 0;
      }
    }
  }, [currentUser]);

  const filteredConversations = useMemo(() => {
    return conversations
      .filter(conv => {
        // Search filter
        if (searchTerm) {
          const other = getOtherParticipant(conv);
          const name = other?.name || other?.email || '';
          if (!name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
        }
        
        // Status filter
        if (selectedStatus === 'unread') {
          return (unreadCounts[conv.id] || 0) > 0;
        } else if (selectedStatus === 'admin') {
          return conv.isAdminRequest;
        } else if (selectedStatus === 'active') {
          return !conv.isAdminRequest;
        }
        
        return true;
      })
      .sort((a, b) => {
        // Prioritize unread messages
        const aUnread = unreadCounts[a.id] || 0;
        const bUnread = unreadCounts[b.id] || 0;
        if (aUnread !== bUnread) return bUnread - aUnread;
        
        // Then by timestamp
        return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
      });
  }, [conversations, searchTerm, selectedStatus, unreadCounts]);

  const getOtherParticipant = (conversation) => {
    if (!conversation || !currentUser) return null;

    if (conversation.isAdminRequest) {
      return {
        name: 'Customer Request',
        email: conversation.buyer?.email
      };
    }

    if (conversation.buyer?.id === currentUser.id) return conversation.seller;
    if (conversation.seller?.id === currentUser.id) return conversation.buyer;

    return conversation.buyer || conversation.seller;
  };

  const getActiveConversationMessages = () => {
    const activeConv = conversations.find(c => c.id === activeConversationId);
    if (!activeConv) return [];

    const thread = messageThreads?.find(t => t.id.toString() === activeConversationId?.toString());

    if (thread?.messages) return thread.messages;

    if (activeConv.isAdminRequest) {
      return [{
        id: 'admin_request_msg',
        content: activeConv.initial_message?.replace('ADMIN_REQUEST:', '').trim(),
        sender: { name: 'System' },
        timestamp: activeConv.created_at,
        isAdminRequest: true
      }];
    }

    return [];
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !activeConversationId) return;

    setIsSending(true);

    try {
      const activeConv = conversations.find(c => c.id === activeConversationId);
      
      // Send message using existing API
      if (!activeConv.isAdminRequest) {
        await sendThreadMessage(activeConversationId, inputMessage);
        // Refresh conversation to get new message
        await fetchThreadById(activeConversationId);
      } else {
        sendMessage(inputMessage, true);
      }

      setInputMessage('');
      
      // Mark as read
      setUnreadCounts(prev => ({
        ...prev,
        [activeConversationId]: 0
      }));
      
      // Trigger immediate refresh for real-time feel
      setTimeout(async () => {
        try {
          const threads = await fetchThreadById();
          if (Array.isArray(threads)) {
            const processed = threads.map(t => ({
              ...t,
              isAdminRequest: t.initial_message?.includes('ADMIN_REQUEST:'),
              unreadCount: t.messages?.filter(m => !m.is_read && m.sender?.id !== currentUser.id).length || 0
            }));
            setConversations(processed);
          }
        } catch (error) {
          console.error('Refresh error:', error);
        }
      }, 1000);
      
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  // Enhanced handler functions
  const handleSelectConversation = (conversationId) => {
    setActiveConversationId(conversationId);
    
    // Mark messages as read
    setUnreadCounts(prev => ({
      ...prev,
      [conversationId]: 0
    }));
  };

  const handleAdminTakeover = (conversationId) => {
    setAdminTakeovers(prev => ({
      ...prev,
      [conversationId]: !prev[conversationId]
    }));
  };

  const handleArchiveConversation = (conversationId) => {
    setConversations(prev => prev.filter(conv => conv.id !== conversationId));
    if (activeConversationId === conversationId) {
      setActiveConversationId(null);
    }
  };

  const handleStatusChange = (status) => {
    setSelectedStatus(status);
  };

  const handleQuickReply = (template) => {
    setInputMessage(template.text);
    setShowQuickReplies(false);
  };

  const handleTypingIndicator = (conversationId, isTyping) => {
    setTypingUsers(prev => ({
      ...prev,
      [conversationId]: isTyping
    }));
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = conversations.length;
    const unread = Object.values(unreadCounts).reduce((sum, count) => sum + count, 0);
    const adminRequests = conversations.filter(conv => conv.isAdminRequest).length;
    const active = conversations.filter(conv => !conv.isAdminRequest).length;
    
    return { total, unread, adminRequests, active };
  }, [conversations, unreadCounts]);

  // Real-time polling for messages (no backend changes needed)
  useEffect(() => {
    if (!currentUser) return;

    console.log('🔄 Starting real-time polling for admin chat');
    setConnectionStatus('connected');
    
    // Set up polling interval for real-time updates
    const pollInterval = setInterval(async () => {
      try {
        const threads = await fetchThreadById();
        if (Array.isArray(threads)) {
          const processed = threads.map(t => ({
            ...t,
            isAdminRequest: t.initial_message?.includes('ADMIN_REQUEST:'),
            unreadCount: t.messages?.filter(m => !m.is_read && m.sender?.id !== currentUser.id).length || 0
          }));
          
          // Check for new messages and update state
          setConversations(prev => {
            const newMessages = processed.filter(newConv => {
              const oldConv = prev.find(c => c.id === newConv.id);
              return oldConv && newConv.updated_at !== oldConv.updated_at;
            });
            
            if (newMessages.length > 0) {
              console.log(`📨 Found ${newMessages.length} updated conversations`);
            }
            
            return processed;
          });
        }
      } catch (error) {
        console.error('Polling error:', error);
        setConnectionStatus('disconnected');
      }
    }, 5000); // Poll every 5 seconds for near real-time

    // Cleanup polling on unmount
    return () => {
      clearInterval(pollInterval);
      setConnectionStatus('disconnected');
    };
  }, [currentUser, fetchThreadById]);

  // Initial data loading
  useEffect(() => {
    if (!currentUser) return;

    const load = async () => {
      try {
        // Fetch admin count first
        await fetchAdminCount();
        
        // Then fetch conversations
        const threads = await fetchThreadById();
        const processed = (Array.isArray(threads) ? threads : []).map(t => ({
          ...t,
          isAdminRequest: t.initial_message?.includes('ADMIN_REQUEST:'),
          unreadCount: t.messages?.filter(m => !m.is_read && m.sender?.id !== currentUser.id).length || 0
        }));

        setConversations(processed);
        
        // Set unread counts
        const unreadMap = {};
        processed.forEach(conv => {
          if (conv.unreadCount > 0) {
            unreadMap[conv.id] = conv.unreadCount;
          }
        });
        setUnreadCounts(unreadMap);
      } catch (error) {
        console.error('Error loading conversations:', error);
      }
    };

    load();
  }, [currentUser, fetchThreadById, fetchAdminCount]);

  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeConversationId]);

  return (
    <Container fluid className="admin-chat-container">
      <Row className="h-100">

        {/* LEFT SIDEBAR */}
        <Col lg={6} className="sidebar-col">
          <Card className="admin-chat-card h-100">

            <div className="admin-chat-header">
              <div className="d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FaUserShield className="me-2" />
                  <span className="fw-bold">Admin Support</span>
                  <span 
                    className="admin-count-badge ms-2" 
                    onClick={fetchAdminCount}
                    title={`Current admin count${adminCount === 1 && currentUser?.role === 'admin' ? ' (verified via authentication)' : ' - Click to refresh'}`}
                    style={{ cursor: 'pointer' }}
                  >
                    {adminCount} Admin{adminCount !== 1 ? 's' : ''}
                  </span>
                  {/* Connection Status Indicator */}
                  <div className="connection-status ms-3">
                    <FaCircle className={`status-dot ${connectionStatus}`} />
                    <span className="status-text ms-1">
                      {connectionStatus === 'connected' ? 'Live Updates' : 
                       connectionStatus === 'reconnecting' ? 'Reconnecting...' : 'Offline'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistics Bar */}
            <div className="admin-stats-bar">
              <div className="stat-item">
                <FaComments className="stat-icon" />
                <div className="stat-info">
                  <div className="stat-number">{stats.total}</div>
                  <div className="stat-label">Total</div>
                </div>
              </div>
              <div className="stat-item">
                <FaExclamationTriangle className="stat-icon urgent" />
                <div className="stat-info">
                  <div className="stat-number">{stats.adminRequests}</div>
                  <div className="stat-label">Requests</div>
                </div>
              </div>
              <div className="stat-item">
                <FaCircle className="stat-icon unread" />
                <div className="stat-info">
                  <div className="stat-number">{stats.unread}</div>
                  <div className="stat-label">Unread</div>
                </div>
              </div>
            </div>

            <InputGroup className="p-2">
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search conversations..."
                onChange={e => debouncedSearch(e.target.value)}
                className="search-input"
              />
            </InputGroup>

            {/* Filter Buttons */}
            <div className="filter-buttons p-2">
              <Button
                variant={selectedStatus === 'all' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleStatusChange('all')}
                className="filter-btn me-2"
              >
                <FaComments className="me-1" />
                All
              </Button>
              <Button
                variant={selectedStatus === 'unread' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleStatusChange('unread')}
                className="filter-btn me-2"
              >
                <FaCircle className="me-1" />
                Unread
              </Button>
              <Button
                variant={selectedStatus === 'admin' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => handleStatusChange('admin')}
                className="filter-btn"
              >
                <FaExclamationTriangle className="me-1" />
                Requests
              </Button>
            </div>

            <div className="conversation-list">
              {filteredConversations.length === 0 ? (
                <div className="empty-state">
                  <FaComments className="empty-icon" />
                  <p className="empty-text">No conversations found</p>
                </div>
              ) : (
                filteredConversations.map(conv => {
                  const other = getOtherParticipant(conv);
                  const isActive = conv.id === activeConversationId;
                  const unreadCount = unreadCounts[conv.id] || 0;
                  const isTyping = typingUsers[conv.id];
                  const isOnline = onlineUsers.has(other?.email);

                  return (
                    <div
                      key={conv.id}
                      className={`conversation-item ${isActive ? 'active' : ''} ${conv.isAdminRequest ? 'admin-request' : ''}`}
                      onClick={() => handleSelectConversation(conv.id)}
                    >
                      <div className="avatar">
                        {conv.isAdminRequest ? (
                          <div className="admin-request-avatar">
                            <FaExclamationTriangle />
                          </div>
                        ) : (
                          <FaUser />
                        )}
                        {isOnline && <FaCircle className="online-indicator" />}
                      </div>

                      <div className="conversation-body">
                        <div className="top-row">
                          <strong>
                            {conv.isAdminRequest ? (
                              <><FaExclamationTriangle className="me-1" /> Customer Request</>
                            ) : (
                              other?.name || other?.email || 'Unknown User'
                            )}
                          </strong>
                          <div className="d-flex align-items-center gap-2">
                            {/* Status Badge */}
                            {adminTakeovers[conv.id] ? (
                              <Badge bg="success" size="sm">
                                <FaUserTie className="me-1" />
                                Active
                              </Badge>
                            ) : (
                              <Badge bg="secondary" size="sm">
                                <FaRobot className="me-1" />
                                Bot
                              </Badge>
                            )}
                            
                            {unreadCount > 0 && (
                              <Badge bg="danger" pill>{unreadCount}</Badge>
                            )}
                            <span className="conversation-time">
                              <FaClock className="me-1" />
                              {new Date(conv.updated_at || conv.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                        </div>

                        <div className="preview">
                          {isTyping && (
                            <span className="typing-indicator">
                              <span>typing...</span>
                            </span>
                          )}
                          {!isTyping && (
                            conv.initial_message?.replace('ADMIN_REQUEST:', '').trim() || 
                            conv.messages?.[conv.messages.length - 1]?.content || 
                            'No messages yet'
                          )}
                        </div>
                      </div>

                      <Button 
                        variant="light" 
                        size="sm" 
                        className="conversation-actions"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Show conversation options
                        }}
                      >
                        <FaEllipsisV />
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </Card>
        </Col>

        {/* CENTER CHAT */}
        <Col lg={6} className="chat-area-col">
          <Card className="chat-area-card h-100 d-flex flex-column">

            {activeConversationId ? (
              <>
                <div className="chat-area-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center flex-grow-1">
                      <div className="participant-avatar me-3">
                        {(() => {
                          const conv = conversations.find(c => c.id === activeConversationId);
                          const participant = getOtherParticipant(conv);
                          if (participant?.isAdminRequest) {
                            return (
                              <div className="admin-request-avatar">
                                <FaExclamationTriangle />
                              </div>
                            );
                          }
                          return <FaUser />;
                        })()}
                      </div>
                      <div className="flex-grow-1">
                        <div className="participant-name fw-bold">
                          {(() => {
                            const conv = conversations.find(c => c.id === activeConversationId);
                            const participant = getOtherParticipant(conv);
                            return participant?.isAdminRequest ? (
                              <><FaExclamationTriangle className="me-1" /> Customer Request</>
                            ) : (
                              participant?.name || participant?.email || 'Unknown User'
                            );
                          })()}
                        </div>
                        <div className="participant-status">
                          <FaCircle className="online-dot me-1" />
                          <span>Online</span>
                          {adminTakeovers[activeConversationId] && (
                            <Badge bg="success" className="ms-2" size="sm">
                              <FaUserTie className="me-1" />
                              Admin Active
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      {/* Admin Actions */}
                      <Button 
                        variant={adminTakeovers[activeConversationId] ? "success" : "outline-primary"}
                        size="sm"
                        onClick={() => handleAdminTakeover(activeConversationId)}
                        title={adminTakeovers[activeConversationId] ? "Release Conversation" : "Take Over Conversation"}
                      >
                        <FaUserTie />
                      </Button>
                      <Button 
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleArchiveConversation(activeConversationId)}
                        title="Archive Chat"
                      >
                        <FaArchive />
                      </Button>
                      
                      {/* Communication Actions */}
                      <Button variant="outline-secondary" size="sm" title="Voice Call">
                        <FaPhone />
                      </Button>
                      <Button variant="outline-secondary" size="sm" title="Video Call">
                        <FaVideo />
                      </Button>
                      <Button variant="outline-secondary" size="sm" title="Add Participants">
                        <FaUsers />
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="messages-container flex-grow-1">
                  {/* Typing Indicator */}
                  {typingUsers[activeConversationId] && (
                    <div className="typing-indicator-container">
                      <span className="text-muted">Someone is typing</span>
                      <div className="typing-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  )}
                  
                  {getActiveConversationMessages().map(msg => {
                    const isOwn = msg.sender?.id === currentUser?.id;
                    const isAdminReqMsg = msg.isAdminRequest;

                    return (
                      <div
                        key={msg.id}
                        className={`message ${isOwn ? 'own' : 'other'} ${isAdminReqMsg ? 'admin-request-message' : ''}`}
                      >
                        <div className="message-content">
                          {!isOwn && (
                            <div className="message-sender">
                              {msg.sender?.name || msg.sender?.email || 'System'}
                            </div>
                          )}
                          <div className="message-text">
                            {msg.content}
                          </div>
                          <div className="message-time">
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messageEndRef} />
                </div>

                {/* Enhanced Message Input */}
                <div className="message-input-container">
                  {/* Quick Replies */}
                  {showQuickReplies && (
                    <div className="quick-replies-container">
                      <div className="quick-replies-header">
                        <small className="text-muted">Quick Replies:</small>
                        <Button variant="link" size="sm" onClick={() => setShowQuickReplies(false)}>
                          <FaTimes />
                        </Button>
                      </div>
                      <div className="quick-replies-grid">
                        {quickReplies.map(template => (
                          <Button
                            key={template.id}
                            variant="outline-primary"
                            size="sm"
                            className="quick-reply-btn"
                            onClick={() => handleQuickReply(template)}
                          >
                            {template.text}
                          </Button>
                        ))}
                      </div>
                    </div>
                  )}

                  <Form onSubmit={handleSendMessage} className="message-input-bar">
                    <div className="d-flex align-items-center gap-2">
                      <Button 
                        variant="light" 
                        title="Attach File"
                        className="action-btn"
                      >
                        <FaPaperclip />
                      </Button>
                      <Button 
                        variant="light" 
                        title="Emoji"
                        className="action-btn"
                      >
                        <FaSmile />
                      </Button>
                      <Button 
                        variant="light" 
                        title="Quick Replies"
                        className="action-btn"
                        onClick={() => setShowQuickReplies(!showQuickReplies)}
                      >
                        <FaComments />
                      </Button>

                      <Form.Control
                        placeholder="Type your message..."
                        value={inputMessage}
                        onChange={e => setInputMessage(e.target.value)}
                        className="flex-grow-1 message-input"
                        disabled={isSending}
                        onFocus={() => handleTypingIndicator(activeConversationId, true)}
                        onBlur={() => handleTypingIndicator(activeConversationId, false)}
                      />

                      <Button 
                        type="submit" 
                        variant="primary" 
                        disabled={isSending || !inputMessage.trim()}
                        className="send-btn"
                      >
                        {isSending ? (
                          <Spinner size="sm" animation="border" />
                        ) : (
                          <FaPaperPlane />
                        )}
                      </Button>
                    </div>
                  </Form>
                </div>
              </>
            ) : (
              <div className="empty-chat-state">
                <FaComments size={60} className="mb-3" />
                <h5>Select a Conversation</h5>
                <p className="text-muted">Choose a conversation from the sidebar to start messaging</p>
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default AdminChatPanel;